import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [vapidPublicKey, setVapidPublicKey] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      const supported = 'serviceWorker' in navigator && 
                       'PushManager' in window && 
                       'Notification' in window;
      setIsSupported(supported);
      
      if (supported) {
        setPermission(Notification.permission);
      }
    };

    checkSupport();
  }, []);

  // Initialize service worker and check subscription status
  useEffect(() => {
    if (isSupported && user) {
      initializeNotifications();
    }
    // initializeNotifications is stable for this mount-only subscription setup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupported, user]);

  const initializeNotifications = async () => {
    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      console.log('Service Worker registered:', registration);

      // Get VAPID public key from server
      await fetchVapidKey();

      // Check existing subscription
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        setSubscription(existingSubscription);
        setIsSubscribed(true);
        console.log('Existing subscription found:', existingSubscription);
      }

    } catch (error) {
      console.error('Error initializing notifications:', error);
    }
  };

  const fetchVapidKey = async () => {
    try {
      const response = await axios.get('/notifications/vapid-public-key');
      setVapidPublicKey(response.data.publicKey);
    } catch (error) {
      console.error('Error fetching VAPID key:', error);
    }
  };

  const requestPermission = async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission === 'granted') {
        toast.success('Notification permission granted!');
        return true;
      } else if (permission === 'denied') {
        toast.error('Notification permission denied. You can enable it in browser settings.');
        return false;
      } else {
        toast.error('Notification permission dismissed');
        return false;
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast.error('Error requesting notification permission');
      return false;
    }
  };

  const subscribe = async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported');
      return false;
    }

    if (!user) {
      toast.error('Please log in to subscribe to notifications');
      return false;
    }

    if (!vapidPublicKey) {
      toast.error('Notification configuration not available');
      return false;
    }

    setLoading(true);

    try {
      // Request permission first
      const hasPermission = permission === 'granted' || await requestPermission();
      if (!hasPermission) {
        setLoading(false);
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Create push subscription
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      console.log('Push subscription created:', pushSubscription);

      // Get device info
      const deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        timestamp: new Date().toISOString()
      };

      // Send subscription to server
      const response = await axios.post('/notifications/subscribe', {
        subscription: pushSubscription.toJSON(),
        deviceInfo
      });

      if (response.data) {
        setSubscription(pushSubscription);
        setIsSubscribed(true);
        toast.success('Successfully subscribed to notifications!');
        setLoading(false);
        return true;
      }

    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      toast.error('Failed to subscribe to notifications');
      setLoading(false);
      return false;
    }
  };

  const unsubscribe = async () => {
    if (!subscription) {
      toast.error('No active subscription found');
      return false;
    }

    setLoading(true);

    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Notify server
      await axios.post('/notifications/unsubscribe', {
        endpoint: subscription.endpoint
      });

      setSubscription(null);
      setIsSubscribed(false);
      toast.success('Successfully unsubscribed from notifications');
      setLoading(false);
      return true;

    } catch (error) {
      console.error('Error unsubscribing from notifications:', error);
      toast.error('Failed to unsubscribe from notifications');
      setLoading(false);
      return false;
    }
  };

  const getSubscriptionStatus = async () => {
    if (!isSupported || !user) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      
      if (existingSubscription) {
        setSubscription(existingSubscription);
        setIsSubscribed(true);
      } else {
        setSubscription(null);
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  const testNotification = async () => {
    if (!isSubscribed) {
      toast.error('Please subscribe to notifications first');
      return;
    }

    try {
      // Send a test notification via the server
      const response = await axios.post('/notifications/test', {
        title: '🎉 Test Notification',
        body: 'This is a test notification from Football Betting App!',
        userId: user._id
      });

      if (response.data) {
        toast.success('Test notification sent!');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Failed to send test notification');
    }
  };

  // Utility function to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const value = {
    isSupported,
    isSubscribed,
    subscription,
    permission,
    loading,
    subscribe,
    unsubscribe,
    requestPermission,
    getSubscriptionStatus,
    testNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
